import { useCallback, useEffect, useRef } from 'react';
import { useSetAtom } from 'jotai';
import type { DurationFilterModel } from './types';
import { dashboardFilterAtom } from '../../filterAtom';

export const useDurationFilter = (model: DurationFilterModel | null) => {
  const setDashboardFilter = useSetAtom(dashboardFilterAtom);
  const previousModelReference = useRef<DurationFilterModel | null>(null);

  const updateAtom = useCallback(
    (newModel: DurationFilterModel | null) => {
      if (!newModel) {
        setDashboardFilter((previousFilter) => ({
          ...previousFilter,
          durationStart: undefined,
          durationEnd: undefined,
        }));
        return;
      }

      // For "Less Than" mode, use durationEnd
      // For "Greater Than" mode, use durationStart
      // For "Between" mode, use both
      if (newModel.type === 'lessThan') {
        setDashboardFilter((previousFilter) => ({
          ...previousFilter,
          durationStart: undefined,
          durationEnd: newModel.fromMinutes,
        }));
      } else if (newModel.type === 'greaterThan') {
        setDashboardFilter((previousFilter) => ({
          ...previousFilter,
          durationStart: newModel.fromMinutes,
          durationEnd: undefined,
        }));
      } else if (newModel.type === 'inRange') {
        setDashboardFilter((previousFilter) => ({
          ...previousFilter,
          durationStart: newModel.fromMinutes,
          durationEnd: newModel.toMinutes,
        }));
      }
    },
    [setDashboardFilter]
  );

  useEffect(() => {
    const hasModelChanged =
      JSON.stringify(model) !== JSON.stringify(previousModelReference.current);

    if (hasModelChanged) {
      updateAtom(model);
      previousModelReference.current = model;
    }
  }, [model, updateAtom]);
};
